import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Container,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Checkbox,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  InputAdornment,
  Grid,
  Button,
  CircularProgress
} from '@mui/material'
import makeStyles from "@mui/styles/makeStyles"
import { Search, ExpandMore, ExpandLess } from '@mui/icons-material'
import { useConfirmationDialog } from '../providers/ConfirmationDialogProvider'
import { useSnackbar } from '../providers/SnackbarProvider'

const useStyles = makeStyles((theme) => ({
  pageResalesLicences: {
    minHeight: "calc(100vh - 114px)",
    padding: theme.spacing(3)
  },
  cardResaleLicence: {
    marginTop: theme.spacing(3),
    '&:first-of-type': {
      marginTop: theme.spacing(2)
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: "calc(100vh - 114px)",
  }
}))

export default function ResalesLicences() {
  const classes = useStyles()
  const { openConfirmationDialog } = useConfirmationDialog()
  const { openSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(true)
  const [licences, setLicences] = useState([])
  const [filteredLicences, setFilteredLicences] = useState([])
  const [autocompleteList, setAutocompleteList] = useState([])

  // Função para verificar se a licença está expirada
  const isLicenceExpired = useCallback((expirationDate) => {
    const now = new Date()
    const expiry = new Date(expirationDate)
    return expiry < now
  }, [])

  // Função para carregar as licenças
  const requestList = useCallback(async () => {
    setLoading(true)
    try 
    {
      // Busca as licenças de revenda
      const res = await fetch(`${process.env.REACT_APP_API_URL}/licences/resales/licences`, {
        method: 'GET',
        headers: { "content-type": "application/json" }
      })
      const request = await res.json()
      console.log("Licenças de revenda recebidas:", request)

      const tempList = await Promise.all(
        request.map(async (item, i) => {
          // Busca centrais livres
          let freeClientList = []
          try 
          {
            const resFree = await fetch(`${process.env.REACT_APP_API_URL}/licences/free/serials?id_revenda=${item.id_revenda}`, {
              method: 'GET',
              headers: { "content-type": "application/json" }
            })
            const dataFree = await resFree.json()
            freeClientList = dataFree.map((desktopLicense, idx) => ({
              id: `${item.id}-free-${idx}`,
              position: idx,
              isChecked: false,
              serial: desktopLicense.idCentral,
              key: desktopLicense.chave
            }))
          } 
          catch (error) 
          {
            console.error("Erro ao buscar centrais livres:", error)
            openSnackbar("Erro ao carregar centrais livres", "error")
          }

          // Busca centrais associadas
          let clientList = []
          try 
          {
            const resClient = await fetch(`${process.env.REACT_APP_API_URL}/licences/client/list?id_licenca=${item.id}`, {
              method: 'GET',
              headers: { "content-type": "application/json" }
            })
            const dataClient = await resClient.json()

            clientList = dataClient.serialNumbers.map((serialObj, idx) => ({
              id: `${item.id}`,
              position: idx,
              isChecked: true,
              serial: serialObj,
              key: dataClient.licenseKeys[idx]
            }))
          } 
          catch (error) 
          {
            console.error("Erro ao buscar lista de clientes:", error)
            openSnackbar("Erro ao carregar lista de clientes", "error")
          }

          return {
            id: item.id,
            position: i,
            open: false,
            licence: item,
            freeClientList,
            clientList
          }
        })
      )

      setLicences(tempList)
      setFilteredLicences(tempList)
      setAutocompleteList([...new Set(tempList.map(item => item.licence.revenda))])
    } 
    catch (error) 
    {
      console.error("Erro ao buscar licenças de revenda:", error)
      openSnackbar("Erro ao carregar licenças", "error")
    } 
    finally 
    {
      setLoading(false)
    }
  }, [openSnackbar])

  // Função de filtro por revenda
  const handleAutocompleteChange = useCallback((event, value) => {
    if (!value) {
      setFilteredLicences(licences)
      return
    }
    
    const filtered = licences.filter(item => 
      item.licence.revenda === value
    )
    setFilteredLicences(filtered)
  }, [licences])

  // Funções para abrir/fechar cards
  const toggleLicenceCard = useCallback((itemPos) => {
    setFilteredLicences(prev => prev.map((item, index) =>
      index === itemPos ? { ...item, open: !item.open } : item
    ))
  }, [])

  // Funções para checkboxes
  const handleCheck = useCallback((itemPos, clientPosition) => {
    setFilteredLicences(prev => prev.map((item, index) => {
      if (index !== itemPos) return item
      
      const updatedClientList = [...item.clientList]
      updatedClientList[clientPosition] = {
        ...updatedClientList[clientPosition],
        isChecked: !updatedClientList[clientPosition].isChecked
      }
      
      return { ...item, clientList: updatedClientList }
    }))
  }, [])

  const handleFreeClientCheck = useCallback((itemPos, clientPosition) => {
    setFilteredLicences(prev => prev.map((item, index) => {
      if (index !== itemPos) return item
      
      const updatedFreeClientList = [...item.freeClientList]
      updatedFreeClientList[clientPosition] = {
        ...updatedFreeClientList[clientPosition],
        isChecked: !updatedFreeClientList[clientPosition].isChecked
      }
      
      return { ...item, freeClientList: updatedFreeClientList }
    }))
  }, [])

	// Funções de ativação/desativação
	const handleActivateLicence = useCallback((item) => {
		if (isLicenceExpired(item.licence.expiracao_licenca)) {
			openConfirmationDialog({
				title: "Licença Expirada",
				message: "Não é possível ativar. A licença está vencida.",
				confirmButtonText: "OK",
				confirmButtonColor: "primary"
			})
			return
		}
		
		const checkedCount = item.freeClientList.filter(c => c.isChecked).length
		const message = checkedCount === 0
			? "Nenhuma central foi selecionada para ativação. Deseja prosseguir mesmo assim?"
			: `A licença ${item.licence.chave} será ativada para ${item.licence.revenda}. Deseja prosseguir?`

		openConfirmationDialog({
			title: "Ativar Licença",
			message: message,
			confirmButtonText: "Ativar",
			confirmButtonColor: "primary",
			onConfirm: async () => {
				try {
					const selectedSerials = [
						...item.clientList.filter(c => c.isChecked),
						...item.freeClientList.filter(c => c.isChecked)
					]

					const serialNumbers = selectedSerials.map(item => ({
						serial: item.serial,
						key: item.key,
						isChecked: item.isChecked
					}))
			
					const res = await fetch(`${process.env.REACT_APP_API_URL}/licences/activate/integrator?id_revenda=${item.licence.id_revenda}`, {
						method: 'POST',
						headers: { "content-type": "application/json" },
						body: JSON.stringify({
							licenceId: item.id,
							serialNumbers
						})
					})
			
					if (res.ok) {
						openSnackbar(`Licença ${item.licence.chave} ativada!`, "success")
						requestList() // Recarrega a lista
					} else {
						openSnackbar("Erro ao ativar licença.", "error")
					}
				} 
				catch (error) 
				{
					console.error("Erro ao ativar licença:", error)
					openSnackbar("Erro ao ativar licença.", "error")
				}
			}
		})
	}, [isLicenceExpired, openConfirmationDialog, openSnackbar, requestList])

  const handleDisableLicence = useCallback((item) => {
    if (isLicenceExpired(item.licence.expiracao_licenca)) {
      openConfirmationDialog({
        title: "Licença Expirada",
        message: "A licença está vencida.",
        confirmButtonText: "OK",
        confirmButtonColor: "primary"
      })
      return
    }

    openConfirmationDialog({
        title: "Desabilitar Licença",
        message: `Tem certeza que deseja desabilitar a licença ${item.licence.chave} para ${item.licence.revenda}?`,
        confirmButtonText: "Desabilitar",
        confirmButtonColor: "error",
        onConfirm: async () => {
            try 
            {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/licences/disable/integrator?id_licenca=${item.id}`, {
                    method: 'POST',
                    headers: { "content-type": "application/json" }
                })
                
                if (res.ok) 
                {
                    openSnackbar(`Licença ${item.licence.chave} desabilitada!`, "info")
                    requestList()
                } 
                else 
                {
                    openSnackbar("Erro ao desabilitar licença.", "error")
                }
            } 
            catch (error) 
            {
                console.error("Erro ao desabilitar licença:", error)
                openSnackbar("Erro ao desabilitar licença.", "error")
            }
        }
        })
  }, [isLicenceExpired, openConfirmationDialog, openSnackbar, requestList])

	const handleUpdateLicence = useCallback((item) => {
		if (isLicenceExpired(item.licence.expiracao_licenca)) 
		{
			openConfirmationDialog({
				title: "Licença Expirada",
				message: "Não é possível fazer alterações. A licença está vencida.",
				confirmButtonText: "OK",
				confirmButtonColor: "primary"
			})
			return
		}

		// Verificar limite de 5 séries
		const selectedSerials = [
			...item.clientList.filter(c => c.isChecked),
			...item.freeClientList.filter(c => c.isChecked)
		]

		if (selectedSerials.length > 5) {
			openSnackbar("Esta licença comporta apenas 5 séries de central", "error")
			return
		}

		openConfirmationDialog({
			title: "Alterar Licença",
			message: `Tem certeza que deseja alterar a licença ${item.licence.chave} para ${item.licence.revenda}?`,
			confirmButtonText: "Alterar",
			confirmButtonColor: "primary",
			onConfirm: async () => {
				try 
				{
					const serialNumbers = selectedSerials.map(item => ({
						serial: item.serial,
						key: item.key,
						isChecked: item.isChecked
					}))

					console.log("Serial Numbers to update:", serialNumbers)

					const res = await fetch(`${process.env.REACT_APP_API_URL}/licences/update/list?id_revenda=${item.licence.id_revenda}`, {
						method: 'POST',
						headers: { "content-type": "application/json" },
						body: JSON.stringify({
							licenceId: item.id,
							serialNumbers
						})
					})

					const data = await res.json()
					if (data === true) {
						openSnackbar("Licença alterada com sucesso", "success")
						requestList() // Recarrega a lista
					} else {
						openSnackbar("Erro ao alterar licença. Tente novamente", "error")
					}
				} 
				catch (error) 
				{
					console.error("Erro ao atualizar licença:", error)
					openSnackbar("Erro ao alterar licença. Tente novamente", "error")
				}
			}
		})
	}, [isLicenceExpired, openConfirmationDialog, openSnackbar, requestList])

	// Carregar dados iniciais
	useEffect(() => {
		requestList()
	}, [requestList])

	// Loading state
	if (loading) {
		return (
			<Container className={classes.loadingContainer}>
				<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
					<CircularProgress />
					<Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>
					Carregando...
					</Typography>
				</Box>
			</Container>
		)
	}

  	return (
		<Container className={classes.pageResalesLicences}>
			<Box sx={{ mb: 3 }}>
				<Autocomplete
				disablePortal
				options={autocompleteList}
				onChange={handleAutocompleteChange}
				renderInput={(params) => (
					<TextField
					{...params}
					placeholder="Pesquisar por Integrador"
					InputProps={{
						...params.InputProps,
						startAdornment: (
						<InputAdornment position="start">
							<Search color="secondary" />
						</InputAdornment>
						)
					}}
					/>
				)}
				/>
			</Box>

			{filteredLicences.map((item, itemPos) => (
				<Card className={classes.cardResaleLicence} key={item.id}>
				<CardHeader
					action={
					<Tooltip title={item.open ? "Fechar" : "Abrir"}>
						<IconButton onClick={() => toggleLicenceCard(itemPos)}>
						{item.open ? <ExpandLess /> : <ExpandMore />}
						</IconButton>
					</Tooltip>
					}
					title={
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} sm={6}>
						<Typography variant="subtitle1" noWrap>
							{item.licence.revenda}
						</Typography>
						</Grid>
						<Grid item xs={12} sm={3}>
						<Typography variant="body2" color="textSecondary" noWrap>
							{item.licence.chave}
						</Typography>
						</Grid>
						<Grid item xs={12} sm={3}>
						<Typography variant="body2" color="textSecondary" align='right'>
							Validade: {new Date(item.licence.expiracao_licenca).toLocaleDateString()}
						</Typography>
						</Grid>
					</Grid>
					}
				/>
				
				{item.open && (
					<>
					<CardContent>
						<Grid container spacing={3}>
						{/* Centrais associadas */}
						<Grid item xs={12} md={6}>
							<Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
							<Typography 
								variant="h6" 
								sx={{ 
								position: 'sticky', 
								top: 0, 
								backgroundColor: '#1E1E1E',
								zIndex: 1,
								pb: 1
								}}
							>
								Centrais associadas a esta licença
							</Typography>
							
							{item.clientList.length === 0 ? (
								<Typography color="textSecondary" sx={{ mt: 2 }}>
								Nenhuma central associada.
								</Typography>
							) : (
								item.clientList.map((client) => (
									<Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
									<Checkbox
										checked={client.isChecked}
										onChange={() => handleCheck(itemPos, client.position)}
										inputProps={{ 'aria-label': `Selecionar ${client.serial}` }}
									/>
									<Typography variant="body2" noWrap>
										{client.serial} - (Pro Local: {client.key} )
									</Typography>
									</Box>
								))
							)}
							</Box>
						</Grid>
						
						{/* Centrais livres */}
						<Grid item xs={12} md={6}>
							<Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
							<Typography 
								variant="h6" 
								sx={{ 
								position: 'sticky', 
								top: 0, 
								backgroundColor: '#1E1E1E',
								zIndex: 1,
								pb: 1
								}}
							>
								Centrais Livres
							</Typography>
							
							{item.freeClientList.length === 0 ? (
								<Typography color="textSecondary" sx={{ mt: 2 }}>
								Nenhuma central livre disponível.
								</Typography>
							) : (
								item.freeClientList.map((freeClient) => (
									<Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
									<Checkbox
										checked={freeClient.isChecked}
										onChange={() => handleFreeClientCheck(itemPos, freeClient.position)}
										inputProps={{ 'aria-label': `Selecionar ${freeClient.serial}` }}
									/>
									<Typography variant="body2" noWrap>
										{freeClient.serial} - (Pro Local: {freeClient.key} )
									</Typography>
									</Box>
								))
							)}
							</Box>
						</Grid>
						</Grid>
					</CardContent>
					
					<CardActions sx={{ justifyContent: 'flex-end' }}>
						{item.licence.chave_utilizada ? (
						<>
							<Button
							variant="outlined"
							color="error"
							onClick={() => handleDisableLicence(item)}
							sx={{ ml: 2 }}
							>
							Desabilitar Licença
							</Button>
							<Button
							variant="contained"
							color="secondary"
							onClick={() => handleUpdateLicence(item)}
							>
							Alterar Licença
							</Button>
						</>
						) : (
						<Button
							variant="contained"
							color="secondary"
							onClick={() => handleActivateLicence(item)}
						>
							Ativar Licença
						</Button>
						)}
					</CardActions>
					</>
				)}
				</Card>
			))}
		</Container>
	)
}